import React, { useEffect } from 'react';
import { useContent } from '../contexts/ContentContext';
import { resolvePage } from '../pages';

interface BackToListButtonProps {
    href?: string;
    onClick?: () => void;
    label?: string;
    className?: string;
}

const BackToListButton: React.FC<BackToListButtonProps> = ({
    href,
    onClick,
    label,
    className = ''
}) => {
    const { t } = useContent();
    const saved = window.history.state?.contentList;
    let target = href;
    if (href && saved && typeof saved.href === 'string') {
        try {
            const candidate = new URL(saved.href, window.location.origin);
            const fallback = new URL(href, window.location.origin);
            if (candidate.origin === window.location.origin && candidate.pathname === '/' && !candidate.searchParams.has('view') &&
                resolvePage(candidate.search).definition.id === resolvePage(fallback.search).definition.id) target = candidate.pathname + candidate.search + candidate.hash;
        } catch {}
    }
    const contents = <>
            <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
            >
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span
                className="ml-2 whitespace-normal break-words"
            >
                {label ?? t('common.backToList')}
            </span>
    </>;
    return target ? <a className={`lab-button ${className}`} href={target} onClick={event => {
        if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        if (saved?.href !== target || !Number.isFinite(saved.scrollY)) return;
        event.preventDefault();
        window.history.pushState({ restoreContentY: Math.max(0, saved.scrollY) }, '', target);
        window.dispatchEvent(new PopStateEvent('popstate'));
    }}>{contents}</a> : <button type="button" className={`lab-button ${className}`} onClick={onClick}>{contents}</button>;
};

export function useRestoreListPosition(ready: boolean) {
    useEffect(() => {
        const y = window.history.state?.restoreContentY;
        if (!ready || !Number.isFinite(y)) return;
        const frame = requestAnimationFrame(() => {
            window.scrollTo({ top: y, behavior: 'auto' });
            const next = { ...window.history.state }; delete next.restoreContentY;
            window.history.replaceState(next, '', window.location.href);
        });
        return () => cancelAnimationFrame(frame);
    }, [ready]);
}

export default BackToListButton;
